
import { ToastContainer } from "react-toastify";
import { useDropzone } from "react-dropzone";
import { useForm, SubmitHandler } from "react-hook-form";
import { useSelector, useDispatch } from "react-redux";
import Lottie from "lottie-react";
import React, { useState, useEffect, useRef, } from "react";

import { AppDispatch, RootState } from "../../../core/data/redux/store";
import { bookingLicenseUpload, resetBooking, } from "../../../core/data/redux/action";
import AddressProofValidation from "../../../core/data/modals/addressProofValidation";
import Loader from "../../../core/data/img/car_loading.json";
import Selfie from "../../../core/data/modals/selfie";



const UploadLicense = ({ booking }: { booking: any }) => {
  interface Attachment {
    file: File;
    name: string;
    label: string;
    type: "selfie" | "upload";
  }

  type FormValues = {
    attachments: any; // correct type
  };

  const dispatch = useDispatch<AppDispatch>();

  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [licenseFiles, setLicenseFiles] = useState<Attachment[]>([]);
  const [selfieFiles, setSelfieFiles] = useState<Attachment[]>([]);
  const [addressProofFiles, setAddressProofFiles] = useState<Attachment[]>([]);
  const [isMobile] = useState(window.innerWidth <= 767);
  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [Error, setError] = useState("");
  // const [same, setSame] = useState(false);
  const [showSelfieValid, setShowSelfieValid] = useState(false);
  const [showAddressProofValid, setShowAddressProofValid] = useState(false);

  const {  templateList, user, bookingUploadLicenseLoading, bookingUploadLicenseSuccess, bookingUploadLicenseError } = useSelector(
    (state: RootState) => ({
      user: state.auth.user,
      templateList: state.template.templateList,
      bookingUploadLicenseSuccess: state.booking.bookingUploadLicenseSuccess,
      bookingUploadLicenseError: state.booking.bookingUploadLicenseError,
      bookingUploadLicenseLoading: state.booking.bookingUploadLicenseLoading,
      

    })
  );

  const { handleSubmit, setValue, reset, watch,
  } = useForm<FormValues>({
    defaultValues: {
      attachments: [],
    },
  });

  const attachments: Attachment[] = watch("attachments") || [];

  const openCamera = async () => {
    setIsCameraOpen(true);
    try {
      setError("");
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          width: { ideal: 1920 }, height: { ideal: 1080 }, facingMode: "user"
        }
      });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (error) {
      setError("Permission denied when accessing the camera. Please enable camera access in device settings.");
      console.error("Error accessing camera:", error);
    }
  };

  const onDropLicense = (acceptedFiles: File[]) => {
    const newAttachments: Attachment[] = acceptedFiles.map((file, index) => {
      const extension = file.name.split('.').pop();

      const fileName = `${user?.first_name}_license_${user?.id}_${booking?.id}_${licenseFiles.length + index + 1}.${extension}`;

      const renamedFile = new File([file], fileName, {
        type: file.type,
        lastModified: file.lastModified,
      });

      return {
        file: renamedFile,
        name: renamedFile.name,
        label: 'driving_license',
        type: "upload",
      };
    });

    setLicenseFiles((prevFiles) => [...prevFiles, ...newAttachments]);
    setValue("attachments", [...attachments, ...newAttachments]);
  };



  const onDropAddressProof = (acceptedFiles: File[]) => {
    const newAttachments: Attachment[] = acceptedFiles.map((file, index) => {
      const extension = file.name.split('.').pop();

      const fileName = `${user?.first_name}_address_proof_${user?.id}_${booking?.id}_${addressProofFiles.length + index + 1}.${extension}`;

      const renamedFile = new File([file], fileName, {
        type: file.type,
        lastModified: file.lastModified,
      });

      return {
        file: renamedFile,
        name: renamedFile.name,
        label: 'address_proof',
        type: "upload",
      };
    });

    setAddressProofFiles((prevFiles) => [...prevFiles, ...newAttachments]);
    setValue("attachments", [...attachments, ...newAttachments]);
  };


  const handleSelfieCapture = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;

    if (files && files.length > 0) {
      const newAttachments: Attachment[] = Array.from(files).map((file) => ({
        file: file,
        name: file.name,
        label: 'address_proof',
        type: "selfie",
      }));

      setSelfieFiles((prevFiles) => [...prevFiles, ...newAttachments]);
    }
  };



  const [rotation, setRotation] = useState(0);

  useEffect(() => {
    const handleOrientationChange = () => {
      const orientation = window.screen.orientation?.type || window.orientation;

      if (orientation === "landscape-primary") {
        setRotation(0);   // normal
      } else if (orientation === "landscape-secondary") {
        setRotation(180); // upside down
      } else if (orientation === "portrait-secondary") {
        setRotation(180); // upside down
      } else {
        // portrait-primary
        setRotation(0);   // normal
      }
    };

    window.addEventListener("orientationchange", handleOrientationChange);
    handleOrientationChange(); // Initial run

    return () => {
      window.removeEventListener("orientationchange", handleOrientationChange);
    };
  }, []);



  const captureSelfie = () => {
    if (videoRef.current && canvasRef.current && !Error) {

      const video = videoRef.current;
      const canvas = canvasRef.current;
      const context = canvas.getContext("2d");

      if (context) {
        // Set canvas size to match video size
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;

        // Now draw with correct resolution
        context.drawImage(video, 0, 0, canvas.width, canvas.height);

        canvas?.toBlob((blob) => {
          if (blob) {
            const selfieIndex = selfieFiles.length + 1;
            const fileName = `${user?.first_name}_selfie_${user?.id}_${booking?.id}_${selfieIndex}.png`;

            const selfieFile = new File([blob], fileName, { type: "image/png" });

            const newAttachment: Attachment = {
              file: selfieFile,
              name: selfieFile.name,
              label: 'driving_license',
              type: "selfie",
            };

            setValue("attachments", [...attachments, newAttachment]);
            setSelfieFiles((prevFiles) => [...prevFiles, newAttachment]);
          }
        }, "image/png");

        // const dataUrl = canvasRef.current.toDataURL("image/png");
        // const selfieIndex = selfieFiles.length + 1;
        // const blob = dataURLtoFile(
        //   dataUrl,
        //   `${user?.first_name}_selfie_${user?.id}_${booking?.id}_${selfieIndex}.png`
        // );

        // if (dataUrl) {

        // }

        const stream = videoRef.current.srcObject as MediaStream;
        const tracks = stream?.getTracks();
        tracks?.forEach((track) => track.stop());
        setIsCameraOpen(false);
      }
    }
  };


  const { getRootProps: getLicenseRootProps, getInputProps: getLicenseInputProps } = useDropzone({
    onDrop: onDropLicense,
    accept: { "image/*": [".png", ".jpg", ".jpeg"] },
    multiple: true,
  });

  const { getRootProps: getAddressProofRootProps, getInputProps: getAddressProofInputProps } = useDropzone({
    onDrop: onDropAddressProof,
    accept: { "image/*": [".png", ".jpg", ".jpeg"] },
    multiple: true,
  });

  const removeFile = (fileName: string, type: "license" | "selfie" | "address_proof") => {
    const updatedLicenseFiles = type === "license"
      ? licenseFiles.filter((file) => file.name !== fileName)
      : licenseFiles;

    const updatedSelfieFiles = type === "selfie"
      ? selfieFiles.filter((file) => file.name !== fileName)
      : selfieFiles;

    const updatedAddresssProoFiles = type === "address_proof"
      ? addressProofFiles.filter((file) => file.name !== fileName)
      : addressProofFiles;



    if (type === "license") {
      setLicenseFiles(updatedLicenseFiles);
    } else if (type === "address_proof") {
      setAddressProofFiles(updatedAddresssProoFiles);
    } else if (type === "selfie") {
      setSelfieFiles(updatedSelfieFiles);
    }



    const updatedAttachments = [...updatedAddresssProoFiles, ...updatedLicenseFiles, ...updatedSelfieFiles];

    setValue("attachments", updatedAttachments);
  };



  const onSubmit: SubmitHandler<FormValues> = (data) => {

    const formData = new FormData();

    formData.append('upload_type', booking?.bookingattachments?.find((item: any) => item.type === "license") ? "reupload" : "initial");
    formData.append('booking_id', booking?.id); 
 



    // formData.append('address_proof_matches_license', same ? "1" : "0");


    if (data?.attachments) {
      const filteredAttachments = data.attachments;
      // .filter(
      //   (att: any) => att.label !== (same ? "address_proof" : "")
      // );

      formData.append('license_uploaded', data.attachments.filter((att: any) => att.label === 'driving_license' && att.type === 'upload')?.length > 0 ? "0" : "1");


      filteredAttachments.forEach((attachment: any, index: number) => {
        formData.append(`attachments[${index}][file]`, attachment?.file);
        formData.append(`attachments[${index}][label]`, attachment?.label);
        formData.append(`attachments[${index}][type]`, attachment?.type);
      });

      dispatch(bookingLicenseUpload(formData));
    }

  };


  useEffect(() => {
    reset();
    setLicenseFiles([]);
    setSelfieFiles([]);
    // setSame(booking?.address_proof_matches_license ? true : false)
  }, [booking]);


  const closeModal = () => {
    const modalElement = document.getElementById("upload_license_with_address_proof");
    const backdrop = document.querySelector(".modal-backdrop");
    if (modalElement && modalElement.style.display !== "none") {
      modalElement.classList.remove("show");
      modalElement.style.display = "none";
      modalElement.classList.remove("show", "modal-open");
    }

    document.body.classList.remove("modal-open");
    document.body.style.overflow = "";
    document.body.style.paddingRight = "";

    if (backdrop) {
      backdrop.remove();
    }

  };

  useEffect(() => {
    if (bookingUploadLicenseSuccess) {
      dispatch(resetBooking());
      closeModal()
      reset();
      setLicenseFiles([]);
      setSelfieFiles([]);
    }

  }, [bookingUploadLicenseSuccess, bookingUploadLicenseError]);


  return (
    <>

      <ToastContainer autoClose={3000} limit={1} />
      <div
        className="modal new-modal fade"
        id="upload_license_with_address_proof"
        data-keyboard="false"
        data-backdrop="static"
        tabIndex={-1}
      >
        <div className="modal-dialog modal-dialog modal-lg">
          <div className="modal-content">
            <div className="modal-header pb-1">
              <h4 className="modal-title"> Upload Verification Documents</h4>
              <button
                type="button"
                className="close-btn"  
                data-bs-dismiss="modal" 
              >
                <span>×</span>
              </button>
            </div>
            <div className="modal-body">


              <form onSubmit={handleSubmit(onSubmit)}>

               
                {/*(booking?.license_status === "pending" || booking?.license_status === "rejected") License Upload */}
                {<div className="col-12 px-2">
                  <div
                    {...getLicenseRootProps()}
                    style={{
                      border: licenseFiles?.length === 0 ? "2px dashed #737373" : "2px dashed #ccc",
                      boxShadow: licenseFiles?.length === 0 ? "0 4px 8px 0 rgba(0, 0, 0, 0.2), 0 6px 20px 0 rgba(0, 0, 0, 0.19)" : "none",

                      padding: "8px",
                      cursor: "pointer",
                      marginBottom: "10px",
                      textAlign: "center",
                      opacity: ["pending", "rejected"].includes(booking?.license_status) ?  1: 0.6 ,
                    }}
                  >
                    <input {...getLicenseInputProps()} disabled={!["pending", "rejected"].includes(booking?.license_status)} />
                    <p className="mb-0">{isMobile ? "Click to upload your License(s)" : "Drag & drop your License(s) here, or Click to Select"}</p>
                  </div>
                  <ul>
                    {licenseFiles.map((file: any) => (
                      <li key={file.name} className="mb-1">
                        <img src={URL.createObjectURL(file.file)} width={200} height={200} /> <br />
                        {file.name}{" "}
                        <span style={{ color: "red", cursor: "pointer" }} onClick={() => removeFile(file.name, "license")}>  <i className="feather icon-x-circle " /></span>

                      </li>
                    ))}
                  </ul>

                  <ul>
                    {booking?.bookingattachments?.filter((item: any) => (item.label === "driving_license" && item.captured_type === "upload"))?.map((file: any) => (
                      <li key={file.name} className="mb-1">
                        <img src={file.file_url} width={200} height={200} /> <br />
                        {file.name}{" "}
                        {/* <span style={{ color: "red", cursor: "pointer" }} onClick={() => removeFile(file.name, "license")}>  <i className="feather icon-x-circle " /></span> */}

                      </li>
                    ))}
                  </ul>
                </div>

                }



                {<div className="col-12 px-2 pt-1 mb-0">
                  <div className="col-12 mb-3 px-0">
                    {/* Selfie Upload */}
                    <div onClick={() => {
                      if ((booking?.license_status === "pending" || booking?.license_status === "rejected")) {
                        openCamera()
                      }
                    }}
                      style={{
                        border: licenseFiles?.length > 0 && selfieFiles?.length === 0 ? "2px dashed #737373" : "2px dashed #ccc",
                        boxShadow: licenseFiles?.length > 0 && selfieFiles?.length === 0 ? "0 4px 8px 0 rgba(0, 0, 0, 0.2), 0 6px 20px 0 rgba(0, 0, 0, 0.19)" : "none",
                        padding: "8px",
                        cursor: "pointer",
                        marginBottom: "10px",
                        textAlign: "center",
                        opacity: (booking?.license_status === "pending" || booking?.license_status === "rejected") ? 1 : 0.6,
                      }}
                    >

                      <p className="mb-0">{"Click to take Selfie(s) with your License"}</p>
                    </div>


                    <h6 className="align-items-center mb-1" onClick={() => {
                      setShowSelfieValid(!showSelfieValid)
                    }}>
                      {/* <span className="pe-1"  >Valid forms of License with Selfie  </span>

                      <FaInfoCircle
                        onClick={() => {
                          setShowSelfieValid(!showSelfieValid)
                        }}
                      /> */}


                      Click <a href="#" className="fs-16 text-underline" >here</a> to know approved form of Selfie(s) with your License
                    </h6>


                    {templateList && showSelfieValid && <div className="message-info py-1">  <Selfie templateList={templateList} /> </div>}


                    <div className="text-center mb-2" >

                      {isCameraOpen && (
                        <div>
                          <video ref={videoRef} autoPlay playsInline
                            style={{
                              width: "100%",
                              maxWidth: "300px",
                              marginTop: "10px",
                              transformOrigin: "center center",
                              transform: `rotate(${rotation}deg) scaleX(-1)`,  // mirror for selfie camera

                            }}
                          />
                          <canvas ref={canvasRef} style={{ display: "none" }} />


                          <p className="text-danger">{Error}</p>
                          <button type="button" className="mt-2 p-2 border-0" onClick={captureSelfie} style={{ cursor: "pointer" }}>
                            📸 Capture Selfie
                          </button>
                        </div>
                      )}
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        capture="user"
                        onChange={handleSelfieCapture}
                        style={{ display: "none" }}
                      />
                    </div>

                    <ul>
                      {selfieFiles.map((file: any) => (
                        <li key={file.name} className="mb-1">
                          <img src={URL.createObjectURL(file.file)} width={200} height={200} /> <br />

                          {file.name}{" "}

                          <span style={{ color: "red", cursor: "pointer" }} onClick={() => removeFile(file.name, "selfie")}>  <i className="feather icon-x-circle " /></span>

                        </li>
                      ))}
                    </ul>

                    <ul>
                      {booking?.bookingattachments?.filter((item: any) => (item.label === "driving_license" && item.captured_type === "selfie"))?.map((file: any) => (
                        <li key={file.name} className="mb-1">
                          <img src={file.file_url} width={200} height={200} /> <br />

                          {file.name}{" "}

                          {/* <span style={{ color: "red", cursor: "pointer" }} onClick={() => removeFile(file.name, "selfie")}>  <i className="feather icon-x-circle " /></span> */}

                        </li>
                      ))}
                    </ul>


                  </div>

                </div>}

                {(booking?.address_proof_status === "pending" || booking?.address_proof_status === "rejected") && <> 
                 {/* <div className="col-12 px-2 mb-2">

                  <input type="checkbox" name="same" onChange={(e) => {
                    setSame(e.target.checked);
                  }}
                    disabled={
                      (
                        booking?.address_proof_status !== "pending" &&
                        booking?.address_proof_status !== "rejected"
                      ) ||
                      (
                        booking?.address_proof_status === "pending" &&
                        licenseFiles?.length === 0 &&
                        (booking?.bookingattachments?.filter(
                          (item: any) =>
                            item.label === "driving_license" && item.captured_type === "upload"
                        )?.length ?? 0) === 0
                      )
                    }
                  />
                  <span className="ms-2">Address proof is same as license  </span>

                </div> */}


                  {   <div className="col-12 px-2 mb-2">


                    <div
                      {...getAddressProofRootProps()}
                      style={{
                        border: "2px dashed #ccc",
                        padding: "8px",
                        cursor: "pointer",
                        marginBottom: "10px",
                        textAlign: "center",
                        opacity: (booking?.address_proof_status !== "pending" && booking?.address_proof_status !== "reject") ? 0.6 : 1,
                      }}
                    >
                      <input {...getAddressProofInputProps()}

                        disabled={(booking?.address_proof_status !== "pending" && booking?.address_proof_status !== "reject")}

                      />

                      <p className="mb-0"> {isMobile ? "Click to upload your Address proof" : "Drag & drop your Address proof here, or Click to Select"}</p>

                    </div>

                    <ul>
                      {addressProofFiles?.map((file) => (
                        <li key={file.name} className="mb-1">
                          <img src={URL.createObjectURL(file.file)} width={200} height={200} /> <br />
                          {file.name}  {" "}
                          <span style={{ color: "red", cursor: "pointer" }}
                            onClick={() => removeFile(file.name, "address_proof")}>  <i className="feather icon-x-circle " /></span>

                        </li>
                      ))}
                    </ul>

                    <ul>
                      {booking?.bookingattachments?.filter((item: any) => (item.label === "addess_proof"))?.map((file: any) => (
                        <li key={file.name} className="mb-1">
                          <img src={file.file_url} width={200} height={200} /> <br />

                          {file.name}{" "}

                          {/* <span style={{ color: "red", cursor: "pointer" }} onClick={() => removeFile(file.name, "selfie")}>  <i className="feather icon-x-circle " /></span> */}

                        </li>
                      ))}
                    </ul>


                    <h6 className="align-items-center mb-1" onClick={() => {
                      setShowAddressProofValid(!showAddressProofValid)
                    }}>

                      Click <a href="#" className="fs-16  text-underline">here</a> to know approved form of Address proof

                      {/* <span className="pe-1"  >Valid forms of Address Proof   </span> */}

                      {/* <FaInfoCircle
                        onClick={() => {
                          setShowAddressProofValid(!showAddressProofValid)
                        }}
                      /> */}

                    </h6>


                    {templateList && showAddressProofValid && <div className="message-info py-1"> <AddressProofValidation templateList={templateList} /> </div>}


                  </div>

                  }

                </>}


                <div className="col-12 px-2">
                  {bookingUploadLicenseLoading ?

                    <div className="d-flex justify-content-center align-items-center">
                      <Lottie animationData={Loader} loop={true} style={{ width: 100, height: 100 }} />
                    </div>

                    :
                    <button type="submit" className="btn btn-primary me-1 mt-1">  Upload  </button>
                  }
                </div>

              </form>
            </div>
          </div>
        </div>
      </div>

    </>
  );
};

export default UploadLicense;